import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { getUuid } from '@front10/helpers/dist/uuid';
import { Context } from '@front10/helpers/dist/Context';
import { useLazyLoading } from '../../../containers/_commons/lazyLoadImage';
import { getResizedImage } from '../../../../helpers/images';

import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import { replaceAll } from '../../../../helpers/utils';

const FareItemAirline = (props) => {
  const {
    showSmallLogo,
    showLargeLogo,
    airlineCode,
    showAirlineName,
    airlineLogoStyles,
    airlineLogoLgStyles,
    airlineLogoContainer,
    airlineLogoLgContainer,
    airlineNameStyles,
    airlineLogoLg,
    airlineLogoSm,
    airlineLogoSmPathNotFound,
    airlineLogoLgPathNotFound,
    lazyLoadingEnable,
    imageResizingParams,
    imageResizingParamsLg,
    airlinesNames,
    id
  } = props;

  let newAirlineLogoLg = replaceAll(airlineLogoLg, '\\*\\*airlineIataCode\\*\\*', airlineCode);
  let newAirlineLogoSm = replaceAll(airlineLogoSm, '\\*\\*airlineIataCode\\*\\*', airlineCode);

  const { direction, theme, translate } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const [airlineLogoClassName, lazyLoadingLogoStyles] = useStyles(airlineLogoStyles, {
    direction,
    theme
  });
  const [airlineLogoLgClassName, lazyLoadingLogoLgStyles] = useStyles(airlineLogoLgStyles, {
    direction,
    theme
  });
  const { observer } = useLazyLoading();

  const airlineLogoLgId = `fr-image-${getUuid()}`;
  const airlineLogoSmId = `fr-image-${getUuid()}`;
  const imageLogoRef = useRef(null);
  const imageLogoLgRef = useRef(null);

  let airlineName = '';
  airlinesNames.forEach((item) => {
    if (item.airlineCode === airlineCode) {
      airlineName = translate(item.labelKey, item.defaultValue);
      return;
    }
  });

  useEffect(() => {
    const { current: currentLogo } = imageLogoRef;
    const { current } = imageLogoLgRef;
    if (observer && lazyLoadingEnable) {
      if (currentLogo) {
        observer.observe(currentLogo);
      }
      if (current) {
        observer.observe(current);
      }
    }
  });

  const baseImageStyle = {
    height: `100%`,
    width: `100%`,
    backgroundColor: lazyLoadingEnable ? 'lightgray' : undefined,
    animation: lazyLoadingEnable ? 'pulse 1s infinite ease-in-out' : undefined,
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain'
  };

  return (
    <Box
      display="flex"
      justifyContent="between"
      className={`fare-item-airline ${className}`}
      style={style}
      id={id}
    >
      {(showAirlineName || showSmallLogo) && (
        <Box className="fare-item-airline-icon" display="flex">
          {showSmallLogo && newAirlineLogoSm && (
            <Box
              id={airlineLogoSmId}
              style={{ minWidth: '50px', minHeight: '50px' }}
              {...airlineLogoContainer}
            >
              <Box
                role={airlineName ? 'img' : undefined}
                ariaLabel={airlineName}
                style={{
                  ...baseImageStyle,
                  ...lazyLoadingLogoStyles,
                  backgroundImage: !lazyLoadingEnable
                    ? `url(${getResizedImage(newAirlineLogoSm, imageResizingParams)})`
                    : null
                }}
                dataSrc={newAirlineLogoSm}
                dataDefaultImage={airlineLogoSmPathNotFound}
                focusReferences={imageLogoRef}
                className={airlineLogoClassName}
              />
            </Box>
          )}
          {showAirlineName && (
            <Text align="self-center" {...airlineNameStyles}>
              {airlineName}
            </Text>
          )}
        </Box>
      )}
      {showLargeLogo && newAirlineLogoLg && (
        <Box
          id={airlineLogoLgId}
          style={{ minHeight: '50px' }}
          width="100"
          className="fare-item-airline-logo-lg"
          {...airlineLogoLgContainer}
        >
          <Box
            role={airlineName ? 'img' : undefined}
            ariaLabel={airlineName}
            style={{
              ...baseImageStyle,
              ...lazyLoadingLogoLgStyles,
              backgroundImage: !lazyLoadingEnable
                ? `url(${getResizedImage(newAirlineLogoLg, imageResizingParamsLg)})`
                : null
            }}
            dataSrc={newAirlineLogoLg}
            dataDefaultImage={airlineLogoLgPathNotFound}
            focusReferences={imageLogoLgRef}
            className={airlineLogoLgClassName}
          />
        </Box>
      )}
    </Box>
  );
};
FareItemAirline.propTypes = {
  /**
   * airlineNameStyles props for using into withStyle.
   */
  airlineNameStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * airlineLogoStyles props for using into withStyle.
   */
  airlineLogoStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * airlineLogoLgStyles props for using into withStyle.
   */
  airlineLogoLgStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * airlineLogoContainer props for using into withStyle.
   */
  airlineLogoContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * airlineLogoLgContainer props for using into withStyle.
   */
  airlineLogoLgContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Airline logo for resolutions higher than or equeal to Lg
   */
  airlineLogoLg: PropTypes.string,
  /**
   * Airline logo for resolutions less than or equal to SM
   */
  airlineLogoSm: PropTypes.string,
  /**
   * Airline logo for resolutions less than or equal to SM
   */
  airlineLogoLgPathNotFound: PropTypes.string,
  /**
   * Show airline name.
   */
  showAirlineName: PropTypes.bool,
  /**
   * Show airline logo.
   */
  showSmallLogo: PropTypes.bool,
  /**
   * Show airline logo.
   */
  showLargeLogo: PropTypes.bool,
  /**
   * imageResizingParams params
   */
  imageResizingParams: PropTypes.objectOf(PropTypes.any),
  /**
   * imageResizingParamsLg params
   */
  imageResizingParamsLg: PropTypes.objectOf(PropTypes.any),
  /**
   * Enable lazyLoadingEnable.
   */
  lazyLoadingEnable: PropTypes.bool
};
FareItemAirline.defaultProps = {
  airlineNameStyles: {},
  airlineLogoStyles: {},
  airlineLogoLgStyles: {},
  airlineLogoContainer: {},
  airlineLogoLgContainer: {},
  airlineLogoLg:
    'https://media.jtdwjcwq6f4wp4ce.com/**airlineIataCode**/logos/**airlineIataCode**-large-fc.png',
  airlineLogoSm:
    'https://media.jtdwjcwq6f4wp4ce.com/**airlineIataCode**/logos/**airlineIataCode**-small-fc.png',
  airlineLogoLgPathNotFound:
    'https://res.cloudinary.com/dakp804eh/image/upload/v1561045947/airmodules/Utils/NoImageFound.svg',
  showAirlineName: true,
  showSmallLogo: true,
  showLargeLogo: true,
  imageResizingParams: {
    worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
    height: 50,
    width: 100,
    quality: 80,
    format: 'auto',
    opt: true
  },
  imageResizingParamsLg: {
    worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
    height: 50,
    width: 200,
    quality: 80,
    format: 'auto',
    opt: true
  },
  airlinesNames: [],
  lazyLoadingEnable: true
};

FareItemAirline.componentName = 'FareItemAirline';

export default FareItemAirline;
